import React from "react";
import { Link, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  Send,
  CreditCard,
  Landmark,
  Coins,
  Settings as SettingsIcon,
  ListChecks,
  FileText,
  LogOut,
  ShieldCheck,
  ShieldAlert,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import SignOutButton from "@/components/SignOutButton";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, end: true, tid: "nav-overview" },
  { to: "/app/deposit", label: "Deposit", icon: ArrowDownToLine, tid: "nav-deposit" },
  { to: "/app/withdraw", label: "Withdraw", icon: ArrowUpFromLine, tid: "nav-withdraw" },
  { to: "/app/transfer", label: "Transfer", icon: Send, tid: "nav-transfer" },
  { to: "/app/cards", label: "Cards", icon: CreditCard, tid: "nav-cards" },
  { to: "/app/banks", label: "Banks", icon: Landmark, tid: "nav-banks" },
  { to: "/app/crypto", label: "Crypto", icon: Coins, tid: "nav-crypto" },
  { to: "/app/transactions", label: "Transactions", icon: ListChecks, tid: "nav-transactions" },
  { to: "/app/statements", label: "Statements", icon: FileText, tid: "nav-statements" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, tid: "nav-settings" },
];

export default function AppLayout({ children }) {
  const { user, balance, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#4F4D45]">
        Loading your vault…
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user.name || user.email || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const kycStatus = user.kyc_status || "not_submitted";
  const kycVerified = kycStatus === "approved";
  const kycOnReview = kycStatus === "submitted";
  const kycRejected = kycStatus === "rejected";
  const showKycBanner =
    user.role !== "admin" && !kycVerified && location.pathname !== "/app/verify-identity";

  return (
    <div className="app-dark min-h-screen bg-[#0E1A14] flex text-white">
      {/* Sidebar */}
      <aside className="hidden md:flex w-72 shrink-0 border-r border-white/10 bg-[#0A130E] backdrop-blur-sm flex-col">
        <div className="px-8 pt-8 pb-6">
          <Link to="/app" className="flex items-center gap-3" data-testid="sidebar-logo">
            <Logo variant="mark" size={36} />
            <div>
              <div className="font-display text-xl font-bold tracking-tight text-white">FinVault</div>
              <div className="text-eyebrow text-white/50 mt-0.5">2026 · Banking</div>
            </div>
          </Link>
        </div>
        <nav className="px-4 flex-1 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end, tid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${
                  isActive
                    ? "bg-[#1B3526] text-white"
                    : "text-white/75 hover:bg-white/[0.06] hover:text-white"
                }`
              }
            >
              <Icon className="w-[18px] h-[18px]" strokeWidth={1.6} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-3">
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.name}
                className="w-10 h-10 rounded-full object-cover ring-1 ring-white/10"
                data-testid="user-avatar"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#A85943] text-white flex items-center justify-center font-display font-semibold" data-testid="user-avatar">
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate text-white" data-testid="user-name">{user.name}</div>
              <div className="text-xs text-white/50 truncate">{user.wallet_id}</div>
            </div>
            <button onClick={handleLogout} title="Sign out" data-testid="logout-button" className="text-white/60 hover:text-[#E27A6A] p-2 rounded-md hover:bg-white/[0.06]">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0E1A14]/85 border-b border-white/10">
          <div className="px-6 md:px-10 py-4 flex items-center justify-between gap-4">
            <div className="md:hidden flex items-center gap-2">
              <Logo variant="mark" size={28} />
              <span className="font-display font-bold text-white">FinVault</span>
            </div>
            <div className="hidden md:flex items-center gap-3 text-sm text-white/60">
              <ShieldCheck className="w-4 h-4 text-[#B58957]" />
              <span data-testid="security-status">Secured session · {user.two_factor_enabled ? "2FA active" : "Email verified"}</span>
            </div>
            <div className="flex items-center gap-4">
              <NotificationBell />
              <div className="text-right">
                <div className="text-eyebrow text-white/50">{user.banking_mode === "demo" ? "Demo balance" : "Live balance"}</div>
                <div className="font-display text-lg font-semibold text-white" data-testid="topbar-balance">
                  {balance
                    ? `$${(user.banking_mode === "demo" ? balance.balance_demo : balance.balance_live).toFixed(2)}`
                    : "—"}
                </div>
              </div>
              <Button onClick={handleLogout} variant="ghost" className="md:hidden text-white hover:bg-white/[0.06] hover:text-white" data-testid="mobile-logout-button">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Mobile nav */}
        <nav className="md:hidden flex overflow-x-auto gap-2 px-4 py-3 border-b border-white/10 bg-[#0A130E]">
          {NAV.map(({ to, label, icon: Icon, end, tid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`${tid}-mobile`}
              className={({ isActive }) =>
                `shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                  isActive ? "bg-[#1B3526] text-white" : "bg-white/[0.06] border border-white/10 text-white/85"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.6} />
              {label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 p-6 md:p-10 max-w-[1280px] w-full mx-auto text-white">
          {showKycBanner && (
            <div
              className={`mb-6 fv-card p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 border ${
                kycRejected
                  ? "border-[#E27A6A]/50"
                  : kycOnReview
                  ? "border-[#B58957]/40"
                  : "border-[#B58957]/50"
              }`}
              data-testid="kyc-banner"
            >
              {kycRejected ? (
                <ShieldAlert className="w-5 h-5 text-[#E27A6A] shrink-0" />
              ) : kycOnReview ? (
                <ShieldCheck className="w-5 h-5 text-[#B58957] shrink-0" />
              ) : (
                <ShieldAlert className="w-5 h-5 text-[#B58957] shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white">
                  {kycRejected
                    ? "Identity verification declined"
                    : kycOnReview
                    ? "Identity verification under review"
                    : "Verify your identity to unlock full access"}
                </div>
                <div className="text-xs text-white/65 mt-0.5">
                  {kycRejected
                    ? "Please contact support@finvault.capital or resubmit."
                    : kycOnReview
                    ? "Our compliance team is reviewing your documents. You'll be notified within 24 hours."
                    : "Deposits, transfers, withdrawals, and crypto require KYC approval."}
                </div>
              </div>
              {!kycOnReview && (
                <Button
                  onClick={() => navigate("/app/verify-identity")}
                  className="bg-[#B58957] hover:bg-[#9C7548] text-[#0A130E] font-medium"
                  data-testid="kyc-banner-cta"
                >
                  {kycRejected ? "Resubmit KYC" : "Verify now"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          )}
          {children}
        </main>

        <footer className="px-6 md:px-10 py-6 border-t border-white/10 text-xs text-white/55 flex flex-wrap gap-x-6 gap-y-2">
          <span>© 2026 FinVault</span>
          <Link to="/about" className="hover:text-white" data-testid="footer-about-link">About</Link>
          <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy-link">Privacy</Link>
          <Link to="/terms" className="hover:text-white" data-testid="footer-terms-link">Terms</Link>
          <Link to="/help" className="hover:text-white" data-testid="footer-help-link">Help</Link>
          <span className="ml-auto">Banking grounded in trust.</span>
        </footer>
      </div>
    </div>
  );
}
